// ==============================================================================
// FILE: src/components/AppLayout.tsx (Updated for Fit-to-Window Layout)
// PURPOSE: Corrects the layout width calculations to prevent horizontal scrollbars
//          and ensure the application perfectly fits the browser window at all times.
//
// CHANGE 1: The `width` calculation for the `AppBar` and the main content `Box`
//           has been changed from `calc(100vw - ...)` to `calc(100% - ...)`.
// REASONING 1: Using `100vw` (viewport width) does not account for the vertical
//              scrollbar's width, causing the layout to be a few pixels too wide.
//              Using `100%` bases the width on the parent container, which correctly
//              respects the scrollbar, eliminating the overflow.
//
// CHANGE 2: Removed `overflowX: 'auto'` from the main content `Box` as it is
//           no longer necessary. The new global styles in `theme.ts` and the
//           corrected width calculation prevent overflow from happening.
// ==============================================================================
import React, { useState, ReactNode, useMemo } from 'react';
import {
  Box, AppBar, Toolbar, Typography, Drawer, List, ListItem, ListItemButton,
  ListItemIcon, ListItemText, CssBaseline, Button, IconButton, Divider,
  useTheme, Breadcrumbs, Link, useMediaQuery, Badge, Popover, Tooltip,
  CircularProgress, Chip
} from '@mui/material';
import {
  Dashboard as DashboardIcon, FileUpload as FileUploadIcon, RateReview, History as HistoryIcon, Menu as MenuIcon,
  Logout as LogoutIcon, Group as UserManagementIcon, ChevronLeft, ChevronRight, NavigateNext as NavigateNextIcon,
  Assessment as ReportIcon, Security as SuperAdminIcon, Notifications as NotificationsIcon, CheckCircle as SuccessIcon,
  Error as ErrorIcon, CloudUpload as BulkIngestIcon, HourglassTop as JobStatusIcon,
  Description as ContractIcon,
} from '@mui/icons-material';
import { RandstadLogo } from './RandstadLogo';
import { useAppContext, Page } from '../contexts/AppContext';
import { Notification } from '../types';

const drawerWidth = 240; // Base width for desktop
const collapsedDrawerWidth = 72; // Collapsed width for desktop

const coreNavItems = [
  { text: 'Dashboard', icon: <DashboardIcon /> },
  { text: 'Ingest', icon: <FileUploadIcon /> },
  { text: 'Review Queue', icon: <RateReview /> },
  { text: 'Claim History', icon: <HistoryIcon /> },
  { text: 'Reports', icon: <ReportIcon /> },
];

const bulkNavItems = [
  { text: 'Bulk Ingest', icon: <BulkIngestIcon /> },
  { text: 'Bulk Job Status', icon: <JobStatusIcon /> },
];

const adminNavItems = [
  { text: 'User Management', icon: <UserManagementIcon /> },
  { text: 'Contract Master Data', icon: <ContractIcon /> },
];

interface AppLayoutProps {
  children: ReactNode;
}

const NotificationCenter = () => {
  const {
    notifications, markAsRead, setActivePage, setReviewingBatchId,
    setErrorBatchId, setAuditBatchId, markAllAsRead
  } = useAppContext();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const unreadCount = useMemo(() => notifications.filter(n => !n.is_read).length, [notifications]);
  const isInProgress = useMemo(() => notifications.some(n => n.status === 'in_progress'), [notifications]);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = (notification: Notification) => {
    handleClose();
    if (!notification.is_read) {
      markAsRead(notification.id);
    }

    if (notification.ingestion_batch_id) {
      setReviewingBatchId(null);
      setAuditBatchId(null);
      setErrorBatchId(null);

      if (notification.status === 'success') {
        setActivePage('Claim History');
      } else if (notification.status === 'error') {
        setErrorBatchId(notification.ingestion_batch_id);
      }
    }
  };

  const open = Boolean(anchorEl);
  const id = open ? 'notification-popover' : undefined;

  return (
    <>
      <IconButton color="inherit" onClick={handleOpen}>
        <Badge badgeContent={unreadCount} color="error">
          <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <NotificationsIcon />
            {isInProgress && (
              <CircularProgress
                size={32} // Slightly larger than the icon
                color="inherit"
                sx={{
                  position: 'absolute',
                  top: -4,
                  left: -4,
                  zIndex: 1,
                }}
              />
            )}
          </Box>
        </Badge>
      </IconButton>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: { 
            width: 360, 
            maxHeight: 400, 
            display: 'flex', 
            flexDirection: 'column',
            marginTop: '8px'
          }
        }}
      >
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" component="div">Notifications</Typography>
          <Button
            size="small"
            onClick={() => {
              markAllAsRead();
              handleClose();
            }}
            disabled={unreadCount === 0}
          >
            Mark all as read
          </Button>
        </Box>
        <List sx={{ p: 0, flexGrow: 1, overflow: 'auto' }}>
          {notifications.length === 0 ? (
            <ListItem>
              <ListItemText primary="No new notifications" />
            </ListItem>
          ) : (
            notifications.map(notification => (
              <ListItemButton
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                sx={{ backgroundColor: notification.is_read ? 'transparent' : 'action.hover' }}
              >
                <ListItemIcon>
                  {notification.status === 'in_progress' && <CircularProgress size={24} />}
                  {notification.status === 'success' && <SuccessIcon color="success" />}
                  {notification.status === 'error' && <ErrorIcon color="error" />}
                </ListItemIcon>
                <ListItemText
                  primary={notification.message}
                  secondary={new Date(notification.created_at).toLocaleString()}
                />
              </ListItemButton>
            ))
          )}
        </List>
      </Popover>
    </>
  );
};

export default function AppLayout({ children }: AppLayoutProps): JSX.Element {
  const { user, logout, activePage, setActivePage, setReviewingBatchId, isSuperAdmin } = useAppContext();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleCollapseToggle = () => {
    setIsCollapsed(!isCollapsed);
  };

  const handleNavClick = (page: Page) => {
    setReviewingBatchId(null);
    setActivePage(page);
    if (mobileOpen) {
      handleDrawerToggle();
    }
  };

  const drawerContent = (
    <div>
      <Toolbar sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', px: 1, height: '64px' }}>
        <Box sx={{
          width: '100%',
          display: 'flex',
          justifyContent: isCollapsed ? 'center' : 'flex-start',
          alignItems: 'center',
          pl: isCollapsed ? 0 : 1
        }}>
          <RandstadLogo sx={{ color: 'primary.main', height: 28, width: 'auto', opacity: isCollapsed ? 0 : 1, transition: 'opacity 0.3s' }} />
        </Box>
      </Toolbar>
      <Divider />
      <List>
        {coreNavItems.map((item) => (
          <ListItem key={item.text} disablePadding sx={{ display: 'block' }}>
            <ListItemButton
              selected={activePage === item.text}
              onClick={() => handleNavClick(item.text as Page)}
              sx={{
                minHeight: 48,
                justifyContent: isCollapsed ? 'center' : 'initial',
                px: 2.5,
              }}
            >
              <ListItemIcon sx={{ minWidth: 0, mr: isCollapsed ? 'auto' : 3, justifyContent: 'center' }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} sx={{ opacity: isCollapsed ? 0 : 1 }} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      {(user?.role === 'admin' || isSuperAdmin()) && (
        <>
          <Divider />
          <List>
            {bulkNavItems.map((item) => (
              <ListItem key={item.text} disablePadding sx={{ display: 'block' }}>
                <ListItemButton
                  selected={activePage === item.text}
                  onClick={() => handleNavClick(item.text as Page)}
                  sx={{
                    minHeight: 48,
                    justifyContent: isCollapsed ? 'center' : 'initial',
                    px: 2.5,
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 0, mr: isCollapsed ? 'auto' : 3, justifyContent: 'center' }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText primary={item.text} sx={{ opacity: isCollapsed ? 0 : 1 }} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </>
      )}
      {(user?.role === 'admin' || isSuperAdmin()) && (
        <>
          <Divider />
          <List>
            {adminNavItems.map((item) => (
              <ListItem key={item.text} disablePadding sx={{ display: 'block' }}>
                <ListItemButton
                  selected={activePage === item.text}
                  onClick={() => handleNavClick(item.text as Page)}
                  sx={{
                    minHeight: 48,
                    justifyContent: isCollapsed ? 'center' : 'initial',
                    px: 2.5,
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 0, mr: isCollapsed ? 'auto' : 3, justifyContent: 'center' }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText primary={item.text} sx={{ opacity: isCollapsed ? 0 : 1 }} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </>
      )}
    </div>
  );

  const finalDrawerWidth = isMobile ? drawerWidth : (isCollapsed ? collapsedDrawerWidth : drawerWidth);

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${finalDrawerWidth}px)` }, // Use % instead of vw
          ml: { sm: `${finalDrawerWidth}px` },
          transition: 'all 0.3s ease-in-out',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={isMobile ? handleDrawerToggle : handleCollapseToggle}
            sx={{ mr: 2, display: { sm: 'block' } }}
          >
            {isMobile ? <MenuIcon /> : (isCollapsed ? <ChevronRight /> : <ChevronLeft />)}
          </IconButton>

          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, fontSize: isMobile ? '1rem' : '1.25rem' }}>
            Reimbursement AI
            {isSuperAdmin() && <Chip label="Super Admin" color="secondary" size="small" sx={{ ml: 2 }} />}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body1" sx={{ display: { xs: 'none', md: 'block' } }}>
              Welcome, {user?.display_name || 'User'}
            </Typography>
            <NotificationCenter />
            <Button
              color="inherit"
              variant="outlined"
              startIcon={<LogoutIcon />}
              onClick={logout}
            >
              Logout
            </Button>
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{
          width: { sm: finalDrawerWidth },
          flexShrink: { sm: 0 },
          transition: 'all 0.3s ease-in-out',
        }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawerContent}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: finalDrawerWidth,
              overflowX: 'hidden',
              transition: 'all 0.3s ease-in-out',
            },
          }}
          open
        >
          {drawerContent}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${finalDrawerWidth}px)` }, // Use % instead of vw
          minWidth: 0,
          transition: 'all 0.3s ease-in-out',
          boxSizing: 'border-box',
        }}
      >
        <Toolbar />
        {(activePage !== 'Dashboard') && (
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 3 }}>
            <Link
              underline="hover"
              color="inherit"
              href="#"
              onClick={(e) => { e.preventDefault(); handleNavClick('Dashboard'); }}
              sx={{ cursor: 'pointer' }}
            >
              Dashboard
            </Link>
            <Typography color="text.primary">{activePage}</Typography>
          </Breadcrumbs>
        )}

        {children}
      </Box>
    </Box>
  );
}

